// app/javascript/components/LeaveRequests.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDatepicker } from '../hooks/useDatepicker';
import { createDonutChart } from '../charts/donutChart';
import Pagination from './Pagination';
import { hasRole } from '../utils/roleHelper';
import { t } from '../utils/i18n';

const LeaveRequests = ({ onBack, api, user, subView = 'military' }) => {
  const isAdmin = hasRole(user, 'admin');
  const isDpHead = hasRole(user, 'department_head');
  const isPersonnel = hasRole(user, 'personnel');
  const isManager = isAdmin || isDpHead || hasRole(user, 'hr_officer') || hasRole(user, 'hr') || hasRole(user, 'army_commander');

  const [loading, setLoading] = useState(false);
  const donutChartRef = useRef(null);
  const [approvalInbox, setApprovalInbox] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [armyTypes, setArmyTypes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Selection and Bulk Actions state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkRejectModalOpen, setBulkRejectModalOpen] = useState(false);
  const [bulkRejectReasonText, setBulkRejectReasonText] = useState('');

  // Document Upload State
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState(null);
  const [previewDocumentName, setPreviewDocumentName] = useState('');

  // Tab Navigation, Balance & Dynamic Calendar State
  const [activeLeaveTab, setActiveLeaveTab] = useState('inbox');
  const [selectedTimelineRequest, setSelectedTimelineRequest] = useState(null);
  const [selectedBalancePersonnelId, setSelectedBalancePersonnelId] = useState('');
  const [personnelOptions, setPersonnelOptions] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDayModalData, setSelectedDayModalData] = useState(null);
  const [holidays, setHolidays] = useState([]);

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLeaveType, setFilterLeaveType] = useState('all');
  const [filterArmyType, setFilterArmyType] = useState('all');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  let personnelId = (isAdmin || isDpHead) ? user.id : user.army_personnel?.id;

  let perpage = (isAdmin || isDpHead) ? 15 : 5;
  const [itemsPerPage] = useState(perpage);

  const handleDateChange = (fieldName, value) => {
    if (fieldName === 'filter_start_date') setFilterStartDate(value);
    else if (fieldName === 'filter_end_date') setFilterEndDate(value);
    else setFilters(prev => ({ ...prev, [fieldName]: value }));
  };
  useDatepicker(handleDateChange);

  const [formData, setFormData] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    army_type_id: '',
    army_personnel_id: personnelId
  });

  const [filters, setFilters] = useState({
    leave_type_id: '',
    start_date: '',
    end_date: '',
    reason: '',
    army_type_id: '',
    army_personnel_id: personnelId
  });

  const [leaveReports, setLeaveReports] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState({
    total: 0,
    taken: 0,
    remaining: 0
  });
  const [leaveEntitlements, setLeaveEntitlements] = useState([]);

  useEffect(() => {
    getLeaveTypes();
    getArmyTypes();
    initializeCharts();
    getApprovalInbox();
    getLeaveReports();
    getLeaveBalance();
    getPersonnelList();
    getHolidays();
  }, [subView]);

  const getArmyTypes = async () => {
    try {
      const response = await api.get('/army_types');
      setArmyTypes(response || []);
    } catch (error) {
      console.error('Error fetching army types:', error);
    }
  };

  const initializeCharts = async () => {
    if (donutChartRef.current) {
      createDonutChart('leaveChart', { users: 450, synced: 300, unsynced: 120 });
    }
  };

  // Filtered Inbox calculation
  const filteredApprovalInbox = useMemo(() => {
    return approvalInbox.filter((item) => {
      // Extra safeguard for subView force matching
      const itemArmyTypeId = item.army_personnel?.army_type_id || item.army_type_id;
      const itemArmyTypeName = (item.army_personnel?.army_type?.name || item.army_type?.name || '').toLowerCase();
      
      if (subView === 'military' || subView === 'darawiish') {
        const isMilitary = String(itemArmyTypeId) === '3' || itemArmyTypeName.includes('military') || itemArmyTypeName.includes('darawiish');
        if (!isMilitary && itemArmyTypeId) return false;
      } else if (subView === 'police') {
        const isPolice = String(itemArmyTypeId) === '2' || itemArmyTypeName.includes('police');
        if (!isPolice && itemArmyTypeId) return false;
      } else if (subView === 'discipline' || subView === 'custodial' || subView === 'asluubta') {
        const isCustodial = String(itemArmyTypeId) === '1' || itemArmyTypeName.includes('custodial') || itemArmyTypeName.includes('asluubta');
        if (!isCustodial && itemArmyTypeId) return false;
      }

      const name = item.army_personnel?.full_name || '';
      const armyId = item.army_personnel?.army_id || '';
      const rank = item.army_personnel?.rank || '';
      const matchSearch = !searchQuery.trim() ||
        name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        armyId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rank.toLowerCase().includes(searchQuery.toLowerCase());

      const itemStatus = (item.status || '').toLowerCase();
      const matchStatus = filterStatus === 'all' || itemStatus === filterStatus.toLowerCase();

      const leaveTypeId = item.leave_type?.id || item.leave_type_id;
      const leaveTypeName = item.leave_type?.name || item.leave_type;
      const matchLeaveType = filterLeaveType === 'all' ||
        String(leaveTypeId) === String(filterLeaveType) ||
        String(leaveTypeName).toLowerCase() === String(filterLeaveType).toLowerCase();

      const matchArmyType = filterArmyType === 'all' || String(itemArmyTypeId) === String(filterArmyType);

      let matchDates = true;
      if (filterStartDate) {
        matchDates = matchDates && (new Date(item.start_date) >= new Date(filterStartDate));
      }
      if (filterEndDate) {
        matchDates = matchDates && (new Date(item.end_date) <= new Date(filterEndDate));
      }

      return matchSearch && matchStatus && matchLeaveType && matchArmyType && matchDates;
    });
  }, [approvalInbox, searchQuery, filterStatus, filterLeaveType, filterArmyType, filterStartDate, filterEndDate, subView]);

  const totalPages = Math.ceil(filteredApprovalInbox.length / itemsPerPage);

  const paginatedApprovalInbox = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredApprovalInbox.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredApprovalInbox, currentPage, itemsPerPage]);

  // Analytics Stats computation
  const stats = useMemo(() => {
    const total = approvalInbox.length;
    const pending = approvalInbox.filter(i => String(i.status).toLowerCase() === 'pending').length;
    const approved = approvalInbox.filter(i => String(i.status).toLowerCase() === 'approved').length;
    const rejected = approvalInbox.filter(i => ['rejected', 'cancelled'].includes(String(i.status).toLowerCase())).length;
    const onLeave = approvalInbox.filter(i => i.active_now || (String(i.status).toLowerCase() === 'approved' && new Date(i.start_date) <= new Date() && new Date(i.end_date) >= new Date())).length;

    return { total, pending, approved, rejected, onLeave };
  }, [approvalInbox]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      setSelectedFileName('');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('File size exceeds 10MB limit');
      e.target.value = '';
      return;
    }
    setSelectedFileName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedFile(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const startDateInput = document.querySelector('input[name="start_date"]');
      const endDateInput = document.querySelector('input[name="end_date"]');
      const selectElement = document.querySelector('select.customSelect');

      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('/')) {
          const [month, day, year] = dateStr.split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return dateStr;
      };

      const submitData = {
        ...formData,
        leave_type_id: selectElement?.value || formData.leave_type_id,
        start_date: formatDate(startDateInput?.value || formData.start_date),
        end_date: formatDate(endDateInput?.value || formData.end_date),
        army_type_id: user.army_personnel?.army_type?.id || user.army_personnel?.army_type_id || null,
        document_data: selectedFile,
        document_name: selectedFileName
      };

      if (!submitData.leave_type_id) {
        alert(t('please_select_leave_type', 'Please select a leave type'));
        setLoading(false);
        return;
      }
      if (submitData.start_date && submitData.end_date) {
        const startDate = new Date(submitData.start_date);
        const endDate = new Date(submitData.end_date);
        if (endDate < startDate) {
          alert(t('end_date_error', 'To Date cannot be prior to From Date'));
          setLoading(false);
          return;
        }
      }

      const { response, data } = await api.post('/leave_requests', { leave_request: submitData });
      if (response.ok) {
        alert(t('leave_request_submitted', 'Leave request submitted successfully'));
        resetForm();
        getApprovalInbox();
        if (startDateInput) startDateInput.value = '';
        if (endDateInput) endDateInput.value = '';
      } else {
        if (data && data.errors) {
          const errorMessages = Array.isArray(data.errors) ? data.errors.join('\n') : data.errors;
          alert(errorMessages);
        } else if (data && data.error) {
          alert(data.error);
        } else {
          alert(t('failed_create_leave', 'Failed to create leave request'));
        }
      }
    } catch (error) {
      console.error('Failed to save leave request:', error);
      alert('Failed to save leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    const startDateInput = document.querySelector('input[name="start_date"]');
    const endDateInput = document.querySelector('input[name="end_date"]');
    const fileInput = document.querySelector('input[name="leave_document_file"]');
    if (startDateInput) startDateInput.value = '';
    if (endDateInput) endDateInput.value = '';
    if (fileInput) fileInput.value = '';
  };

  const handleLeaveCancel = async (leaveRequestId) => {
    if (!window.confirm('Are you sure you want to cancel this leave request?')) {
      return;
    }
    setLoading(true);
    try {
      const { response } = await api.patch(`/leave_requests/${leaveRequestId}/cancel`);
      if (response.ok) {
        alert('Leave request cancelled successfully');
        getApprovalInbox();
      } else {
        alert('Failed to cancel leave request');
      }
    } catch (error) {
      console.error('Failed to cancel leave request:', error);
      alert('Failed to cancel leave request');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      leave_type_id: '',
      start_date: '',
      end_date: '',
      reason: '',
      army_personnel_id: personnelId
    });
    setSelectedFile(null);
    setSelectedFileName('');
  };

  const getApprovalInbox = async () => {
    try {
      const params = new URLSearchParams({ sub_view: subView });
      const response = await api.get(`/leave_requests?${params}`);
      let items = response.data?.leave_requests || response.leave_requests || [];

      const canViewAll = isAdmin || isDpHead || hasRole(user, 'hr_officer') || hasRole(user, 'hr') || hasRole(user, 'army_commander');
      if (!canViewAll) {
        const myPersonnelId = user?.army_personnel?.id;
        items = items.filter((it) => it?.army_personnel?.id === myPersonnelId);
      }

      setApprovalInbox(items);
    } catch (error) {
      console.error('Error fetching approval inbox:', error);
    }
  };

  const handleApprove = async (leaveRequestId) => {
    try {
      const response = await api.patch(`/leave_requests/${leaveRequestId}/approve`);
      if (response.response.ok) {
        alert('Leave request approved successfully');
        getApprovalInbox();
      } else {
        alert('Failed to approve leave request');
      }
    } catch (error) {
      console.error('Error approving leave request:', error);
    }
  };

  const handleReject = async (leaveRequestId) => {
    if (rejectingId === leaveRequestId) {
      if (!rejectionReason.trim()) {
        alert('Please enter rejection reason');
        return;
      }
      try {
        const response = await api.patch(`/leave_requests/${leaveRequestId}/reject`, {
          rejection_reason: rejectionReason
        });
        if (response.response.ok) {
          alert('Leave request rejected successfully');
          getApprovalInbox();
          setRejectingId(null);
          setRejectionReason('');
        } else {
          alert('Failed to reject leave request');
        }
      } catch (error) {
        console.error('Error rejecting leave request:', error);
        alert('Failed to reject leave request');
      }
    } else {
      setRejectingId(leaveRequestId);
    }
  };

  const handleRevoke = async (leaveRequestId) => {
    const reason = window.prompt('Enter reason for revoking this approved leave:');
    if (reason === null) return;
    setLoading(true);
    try {
      const response = await api.patch(`/leave_requests/${leaveRequestId}/revoke`, { reason });
      if (response.response.ok) {
        alert('Leave request revoked successfully');
        getApprovalInbox();
      } else {
        alert('Failed to revoke leave request');
      }
    } catch (error) {
      console.error('Failed to revoke leave request:', error);
      alert('Failed to revoke leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (leaveRequestId) => {
    if (!window.confirm('Are you sure you want to delete this leave request? This action cannot be undone.')) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.delete(`/leave_requests/${leaveRequestId}`);
      if (response.response?.ok || response.ok) {
        alert('Leave request deleted successfully');
        setSelectedIds(prev => prev.filter(id => id !== leaveRequestId));
        getApprovalInbox();
      } else {
        const data = response.data;
        alert((data && data.error) || 'Failed to delete leave request');
      }
    } catch (error) {
      console.error('Error deleting leave request:', error);
      alert('Failed to delete leave request');
    } finally {
      setLoading(false);
    }
  };

  // Bulk Action Processing
  const handleBulkAction = async (actionType, extraReason = '') => {
    if (selectedIds.length === 0) {
      alert('Please select at least one leave request');
      return;
    }

    if (actionType === 'delete') {
      if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected leave requests?`)) {
        return;
      }
    } else if (actionType === 'reject' && !extraReason) {
      setBulkRejectModalOpen(true);
      return;
    } else if (actionType === 'approve') {
      if (!window.confirm(`Approve ${selectedIds.length} selected leave requests?`)) {
        return;
      }
    }

    setLoading(true);
    try {
      const { response, data } = await api.post('/leave_requests/bulk_action', {
        bulk_action_type: actionType,
        ids: selectedIds,
        rejection_reason: extraReason
      });

      if (response.ok) {
        alert(data?.message || `Bulk ${actionType} completed successfully`);
        setSelectedIds([]);
        setBulkRejectModalOpen(false);
        setBulkRejectReasonText('');
        getApprovalInbox();
      } else {
        alert(data?.error || `Failed to execute bulk ${actionType}`);
      }
    } catch (error) {
      console.error(`Error in bulk ${actionType}:`, error);
      alert(`Error executing bulk ${actionType}`);
    } finally {
      setLoading(false);
    }
  };

  // Selection toggles
  const allPaginatedIds = useMemo(() => paginatedApprovalInbox.map(i => i.id), [paginatedApprovalInbox]);
  const isAllSelected = useMemo(() => {
    return allPaginatedIds.length > 0 && allPaginatedIds.every(id => selectedIds.includes(id));
  }, [allPaginatedIds, selectedIds]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIds(prev => prev.filter(id => !allPaginatedIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...allPaginatedIds])));
    }
  };

  const toggleSelectItem = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // CSV Export
  const exportToCSV = () => {
    if (!filteredApprovalInbox || filteredApprovalInbox.length === 0) {
      alert('No leave records to export');
      return;
    }

    const headers = ['ID', 'Personnel Name', 'Rank', 'Leave Type', 'Start Date', 'End Date', 'Status', 'Workflow Status', 'Reason', 'Document Attached'];
    const rows = filteredApprovalInbox.map(item => [
      item.id,
      `"${item.army_personnel?.full_name || 'N/A'}"`,
      `"${item.army_personnel?.rank || 'N/A'}"`,
      `"${item.leave_type?.name || 'N/A'}"`,
      item.start_date || '',
      item.end_date || '',
      item.status || '',
      `"${item.workflow_status || 'N/A'}"`,
      `"${(item.reason || '').replace(/"/g, '""')}"`,
      item.document_path ? 'Yes' : 'No'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Leave_Inbox_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetFilters = () => {
    setSearchQuery('');
    setFilterStatus('all');
    setFilterLeaveType('all');
    setFilterArmyType('all');
    setFilterStartDate('');
    setFilterEndDate('');
    setCurrentPage(1);
  };

  const getLeaveReports = async () => {
    try {
      const response = await api.get('/leave_requests/leave_report');
      setLeaveReports(response.data?.reports || []);
    } catch (error) {
      console.error('Error fetching leave reports:', error);
    }
  };

  const getHolidays = async () => {
    try {
      const response = await api.get('/holiday_calendars');
      const resData = response.data || response;
      setHolidays(resData.holidays || []);
    } catch (error) {
      console.error('Error fetching holiday calendar:', error);
    }
  };

  const getPersonnelList = async () => {
    try {
      const response = await api.get('/leave_requests/personnel_list');
      const resData = response.data || response;
      setPersonnelOptions(resData.personnel || resData || []);
    } catch (error) {
      console.error('Error fetching personnel list:', error);
    }
  };

  const getLeaveBalance = async (targetId = selectedBalancePersonnelId) => {
    try {
      const url = targetId ? `/leave_requests/balance?army_personnel_id=${targetId}` : '/leave_requests/balance';
      const response = await api.get(url);
      const resData = response.data || response;
      setLeaveBalance(resData.balance || { total: 0, taken: 0, remaining: 0 });
      setLeaveEntitlements(resData.entitlements || []);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };

  const getLeaveTypes = async () => {
    try {
      const response = await api.get('/leave_types');
      setLeaveTypes(response.data?.leave_types || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  const getStatusClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'active': return 'leaveActive';
      case 'approved': return 'leaveApproved';
      case 'rejected': return 'leaveRejected';
      case 'cancelled': return 'leaveRejected';
      default: return 'leaveActive';
    }
  };

  return (
    <>
      <div className="mainContentCT dBlock">

        {/* Analytics Summary Banner (Payroll style) */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Card 1: Total Requests */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f4f6f9 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            padding: '16px 20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Total Requests
              </div>
              <div style={{ fontSize: '26px', fontWeight: 700, color: '#1e293b', marginTop: '4px' }}>
                {stats.total}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>Inbox Applications</div>
            </div>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '10px',
              background: '#eff6ff',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                <path d="M9 12h6"></path>
                <path d="M9 16h6"></path>
              </svg>
            </div>
          </div>

          {/* Card 2: Pending Approvals */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fffbeb 100%)',
            border: '1px solid #fef3c7',
            borderRadius: '10px',
            padding: '16px 20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#d97706', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Pending Review
              </div>
              <div style={{ fontSize: '26px', fontWeight: 700, color: '#b45309', marginTop: '4px' }}>
                {stats.pending}
              </div>
              <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>Awaiting Approval</div>
            </div>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '10px',
              background: '#fef3c7',
              color: '#d97706',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
          </div>

          {/* Card 3: Approved Leaves */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
            border: '1px solid #dcfce7',
            borderRadius: '10px',
            padding: '16px 20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Approved
              </div>
              <div style={{ fontSize: '26px', fontWeight: 700, color: '#15803d', marginTop: '4px' }}>
                {stats.approved}
              </div>
              <div style={{ fontSize: '11px', color: '#22c55e', marginTop: '2px' }}>Granted Requests</div>
            </div>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '10px',
              background: '#dcfce7',
              color: '#16a34a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
          </div>

          {/* Card 4: Rejected / Cancelled */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
            border: '1px solid #fee2e2',
            borderRadius: '10px',
            padding: '16px 20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Rejected / Cancelled
              </div>
              <div style={{ fontSize: '26px', fontWeight: 700, color: '#b91c1c', marginTop: '4px' }}>
                {stats.rejected}
              </div>
              <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '2px' }}>Closed Applications</div>
            </div>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '10px',
              background: '#fee2e2',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
          </div>

          {/* Card 5: Currently On Leave */}
          <div style={{
            background: 'linear-gradient(135deg, #ffffff 0%, #faf5ff 100%)',
            border: '1px solid #f3e8ff',
            borderRadius: '10px',
            padding: '16px 20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#9333ea', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                On Leave Today
              </div>
              <div style={{ fontSize: '26px', fontWeight: 700, color: '#7e22ce', marginTop: '4px' }}>
                {stats.onLeave}
              </div>
              <div style={{ fontSize: '11px', color: '#a855f7', marginTop: '2px' }}>Active Absences</div>
            </div>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '10px',
              background: '#f3e8ff',
              color: '#9333ea',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
          </div>
        </div>

        <div className="leaveTopRow">
          {isPersonnel && (
            <div className="leaveRequestSidebar baseTableFormSec">
              <div className="dFlex baseTableHd mb_8">
                <h2 className="txt20 fwMedium colorBlack">Apply Leave (Personnel)</h2>
              </div>
              <form onSubmit={handleSubmit} className="baseTableInpts">
                <ul className="formFieldsWrap" style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  <li className="w_1_3" style={{ flex: '1 1 calc(33.333% - 11px)', minWidth: '200px' }}>
                    <div className="formInpt">
                      <label className="mb_10">Leave Type</label>
                      <select
                        className="customSelect"
                        value={formData.leave_type_id}
                        onChange={(e) => setFormData({ ...formData, leave_type_id: e.target.value })}
                        name="leave_type_id"
                        required
                      >
                        <option value=''>Select Leave Type</option>
                        {leaveTypes.map((type) => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    </div>
                  </li>
                  <li className="w_1_3" style={{ flex: '1 1 calc(33.333% - 11px)', minWidth: '200px' }}>
                    <div className="formInpt hasDatePicker">
                      <label className="mb_10" htmlFor="dateFrom">From</label>
                      <input name="start_date" type="text" className="customDatepicker" placeholder="Select Date" defaultValue={filters.start_date} />
                    </div>
                  </li>
                  <li className="w_1_3" style={{ flex: '1 1 calc(33.333% - 11px)', minWidth: '200px' }}>
                    <div className="formInpt hasDatePicker">
                      <label className="mb_10">To</label>
                      <input name="end_date" type="text" className="customDatepicker" placeholder="Select Date" defaultValue={filters.end_date} />
                    </div>
                  </li>
                  <li className="w_1_1" style={{ flex: '1 1 100%' }}>
                    <div className="formInpt">
                      <label className="mb_10">Reason</label>
                      <textarea
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        rows="3"
                        required
                      />
                    </div>
                  </li>
                  <li className="w_1_1" style={{ flex: '1 1 100%' }}>
                    <div className="formInpt">
                      <label className="mb_10">Supporting Document (Optional)</label>
                      <input
                        type="file"
                        name="leave_document_file"
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                        onChange={handleFileChange}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fff' }}
                      />
                      {selectedFileName && (
                        <div style={{ marginTop: '4px', fontSize: '12px', color: '#2563eb', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <i className="fa-solid fa-paperclip"></i> Attached: {selectedFileName}
                        </div>
                      )}
                    </div>
                  </li>
                </ul>
                <div className="dFlex mt_16 btnSet">
                  <input type="submit" value="Submit" className="btn" disabled={loading} />
                  <button type="button" className="btn btnBlack" onClick={handleCancel}>Cancel</button>
                </div>
              </form>
            </div>
          )}

        </div>

        {/* Module Sub-Tabs Navigation */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', paddingBottom: '8px' }}>
          <button
            type="button"
            onClick={() => setActiveLeaveTab('inbox')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              fontSize: '13px',
              backgroundColor: activeLeaveTab === 'inbox' ? '#1E40AF' : 'transparent',
              color: activeLeaveTab === 'inbox' ? '#ffffff' : '#64748b',
              cursor: 'pointer'
            }}
          >
            📋 Leave Inbox & Requests
          </button>
          <button
            type="button"
            onClick={() => setActiveLeaveTab('calendar')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              fontSize: '13px',
              backgroundColor: activeLeaveTab === 'calendar' ? '#1E40AF' : 'transparent',
              color: activeLeaveTab === 'calendar' ? '#ffffff' : '#64748b',
              cursor: 'pointer'
            }}
          >
            📅 Leave Calendar
          </button>
          <button
            type="button"
            onClick={() => setActiveLeaveTab('dashboard')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              fontSize: '13px',
              backgroundColor: activeLeaveTab === 'dashboard' ? '#1E40AF' : 'transparent',
              color: activeLeaveTab === 'dashboard' ? '#ffffff' : '#64748b',
              cursor: 'pointer'
            }}
          >
            📊 Balance Dashboard & Carry Forward
          </button>
          <button
            type="button"
            onClick={() => setActiveLeaveTab('timeline')}
            style={{
              padding: '8px 16px',
              borderRadius: '6px',
              border: 'none',
              fontWeight: 600,
              fontSize: '13px',
              backgroundColor: activeLeaveTab === 'timeline' ? '#1E40AF' : 'transparent',
              color: activeLeaveTab === 'timeline' ? '#ffffff' : '#64748b',
              cursor: 'pointer'
            }}
          >
            ⏱️ Approval Timeline
          </button>
        </div>

        {activeLeaveTab === 'calendar' && (() => {
          const year = calendarDate.getFullYear();
          const month = calendarDate.getMonth();
          const monthName = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' });
          const daysInMonth = new Date(year, month + 1, 0).getDate();
          const firstDayOfWeek = new Date(year, month, 1).getDay();

          const prevMonth = () => setCalendarDate(new Date(year, month - 1, 1));
          const nextMonth = () => setCalendarDate(new Date(year, month + 1, 1));
          const resetToToday = () => setCalendarDate(new Date());

          return (
            <div className="card-dense" style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '8px', marginBottom: '20px' }}>
              {/* Calendar Controls Header & Legend */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                    📅 Dynamic Leave & Holiday Calendar ({monthName})
                  </h3>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
                    Visual tracking of official holidays & active personnel on approved leave during the selected month.
                  </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={prevMonth}
                    style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: '#ffffff', cursor: 'pointer' }}
                  >
                    ◀ Prev Month
                  </button>
                  <button
                    type="button"
                    onClick={resetToToday}
                    style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, border: '1px solid #1E40AF', borderRadius: '6px', backgroundColor: '#1E40AF', color: '#ffffff', cursor: 'pointer' }}
                  >
                    Today
                  </button>
                  <button
                    type="button"
                    onClick={nextMonth}
                    style={{ padding: '6px 12px', fontSize: '13px', fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: '#ffffff', cursor: 'pointer' }}
                  >
                    Next Month ▶
                  </button>
                </div>
              </div>

              {/* Legend Bar */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', color: '#475569', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#eff6ff', border: '2px solid #1E40AF', display: 'inline-block' }}></span>
                  <strong style={{ color: '#1e3a8a' }}>Today</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#dcfce7', border: '1px solid #15803d', display: 'inline-block' }}></span>
                  <strong style={{ color: '#15803d' }}>Approved Leave</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '12px', height: '12px', borderRadius: '3px', background: '#ffedd5', border: '1px solid #c2410c', display: 'inline-block' }}></span>
                  <strong style={{ color: '#c2410c' }}>Official Holiday</strong>
                </div>
              </div>

              {/* Month Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', textAlign: 'center' }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} style={{ fontWeight: 700, padding: '10px 8px', background: '#f1f5f9', borderRadius: '6px', fontSize: '12px', color: '#475569', textTransform: 'uppercase' }}>
                    {d}
                  </div>
                ))}

                {/* Leading Blank Cells for Day-of-Week offset */}
                {Array.from({ length: firstDayOfWeek }).map((_, idx) => (
                  <div key={`blank-${idx}`} style={{ minHeight: '80px', padding: '6px', border: '1px border-dashed #f1f5f9', borderRadius: '6px', background: '#fafafa' }} />
                ))}

                {/* Days of Month */}
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                  const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();

                  // Filter official holidays covering this specific date
                  const holidaysOnDay = holidays.filter(h => {
                    if (!h.holiday_date) return false;
                    const hDate = h.holiday_date.split('T')[0];
                    if (hDate === dateStr) return true;
                    if (h.recurring) {
                      const hParts = hDate.split('-');
                      const cParts = dateStr.split('-');
                      return hParts[1] === cParts[1] && hParts[2] === cParts[2];
                    }
                    return false;
                  });

                  // Filter real approved leave requests covering this specific date
                  const leavesOnDay = approvalInbox.filter(r => {
                    if (r.status !== 'approved') return false;
                    if (!r.start_date || !r.end_date) return false;
                    const start = r.start_date.split('T')[0];
                    const end = r.end_date.split('T')[0];
                    return dateStr >= start && dateStr <= end;
                  });

                  const hasHoliday = holidaysOnDay.length > 0;
                  const hasLeave = leavesOnDay.length > 0;

                  return (
                    <div
                      key={day}
                      onClick={() => (hasLeave || hasHoliday) && setSelectedDayModalData({ dateStr, leaves: leavesOnDay, holidays: holidaysOnDay })}
                      style={{
                        minHeight: '85px',
                        padding: '8px',
                        border: isToday
                          ? '2px solid #1E40AF'
                          : hasHoliday
                          ? '1px solid #f97316'
                          : hasLeave
                          ? '1px solid #22c55e'
                          : '1px solid #e2e8f0',
                        borderRadius: '6px',
                        textAlign: 'left',
                        background: isToday
                          ? '#eff6ff'
                          : hasHoliday
                          ? '#fff7ed'
                          : hasLeave
                          ? '#f0fdf4'
                          : '#ffffff',
                        cursor: (hasLeave || hasHoliday) ? 'pointer' : 'default',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: isToday ? '#1E40AF' : hasHoliday ? '#c2410c' : '#475569' }}>
                          {day}
                        </span>
                        {isToday && (
                          <span style={{ fontSize: '9px', background: '#1E40AF', color: '#fff', padding: '1px 4px', borderRadius: '3px', fontWeight: 600 }}>
                            TODAY
                          </span>
                        )}
                      </div>

                      {/* Display Official Holidays on this date */}
                      {holidaysOnDay.map((hol, idx) => (
                        <div
                          key={`hol-${idx}`}
                          title={`Official Holiday: ${hol.name} ${hol.location?.name ? `(${hol.location.name})` : ''}`}
                          style={{
                            fontSize: '10px',
                            background: '#ffedd5',
                            color: '#c2410c',
                            border: '1px solid #fed7aa',
                            padding: '3px 6px',
                            borderRadius: '4px',
                            marginTop: '4px',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {hol.name}
                        </div>
                      ))}

                      {/* Display Real Approved Leaves on this date */}
                      {leavesOnDay.map((lvl, idx) => (
                        <div
                          key={`lvl-${idx}`}
                          title={`${lvl.army_personnel?.full_name || 'Officer'} - ${lvl.leave_type?.name || 'Leave'}`}
                          style={{
                            fontSize: '10px',
                            background: '#dcfce7',
                            color: '#15803d',
                            border: '1px solid #bbf7d0',
                            padding: '3px 6px',
                            borderRadius: '4px',
                            marginTop: '4px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {lvl.army_personnel?.full_name?.split(' ')[0] || 'Personnel'} ({lvl.leave_type?.name || 'Leave'})
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>

              {/* Day Click Detail Modal / Popover */}
              {selectedDayModalData && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                  <div style={{ backgroundColor: '#fff', borderRadius: '8px', padding: '24px', maxWidth: '520px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                        📅 Calendar Details ({selectedDayModalData.dateStr})
                      </h4>
                      <button
                        type="button"
                        onClick={() => setSelectedDayModalData(null)}
                        style={{ border: 'none', background: 'none', fontSize: '18px', cursor: 'pointer', color: '#64748b' }}
                      >
                        ✕
                      </button>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '350px', overflowY: 'auto' }}>
                      {/* Official Holidays Section */}
                      {selectedDayModalData.holidays && selectedDayModalData.holidays.length > 0 && (
                        <div>
                          <h5 style={{ fontSize: '13px', fontWeight: 700, color: '#c2410c', marginBottom: '8px' }}>
                            🎉 Official Holidays ({selectedDayModalData.holidays.length})
                          </h5>
                          {selectedDayModalData.holidays.map((h, idx) => (
                            <div key={`h-modal-${idx}`} style={{ padding: '10px', border: '1px solid #fed7aa', borderRadius: '6px', backgroundColor: '#fff7ed', marginBottom: '6px' }}>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: '#9a3412' }}>
                                {h.name} {h.recurring ? '🔄 (Annual Recurring)' : ''}
                              </div>
                              {h.description && (
                                <div style={{ fontSize: '12px', color: '#c2410c', marginTop: '2px' }}>
                                  {h.description}
                                </div>
                              )}
                              {h.location?.name && (
                                <div style={{ fontSize: '11px', color: '#ea580c', marginTop: '2px' }}>
                                  📍 Location: {h.location.name}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Personnel on Approved Leave Section */}
                      {selectedDayModalData.leaves && selectedDayModalData.leaves.length > 0 && (
                        <div>
                          <h5 style={{ fontSize: '13px', fontWeight: 700, color: '#1e3a8a', marginBottom: '8px' }}>
                            👤 Officers On Leave ({selectedDayModalData.leaves.length})
                          </h5>
                          {selectedDayModalData.leaves.map((l, i) => (
                            <div key={`l-modal-${i}`} style={{ padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f8fafc', marginBottom: '6px' }}>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: '#1E40AF' }}>
                                {l.army_personnel?.full_name} ({l.army_personnel?.rank || 'N/A'})
                              </div>
                              <div style={{ fontSize: '12px', color: '#475569', marginTop: '2px' }}>
                                <strong>Leave Type:</strong> {l.leave_type?.name || 'Leave'}
                              </div>
                              <div style={{ fontSize: '12px', color: '#475569' }}>
                                <strong>Duration:</strong> {l.start_date} to {l.end_date}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: '20px', textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setSelectedDayModalData(null)}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {activeLeaveTab === 'dashboard' && (
          <div className="card-dense" style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '8px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', margin: 0 }}>Leave Balance & Carry Forward Summary</h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>
                  Real-time individual personnel calculations based on approved leave requests & year-end carry forward rules.
                </p>
              </div>

              {/* Individual Personnel Selector Dropdown for Managers/Admins */}
              {(isAdmin || isDpHead || isManager) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                    Select Personnel:
                  </label>
                  <select
                    value={selectedBalancePersonnelId}
                    onChange={(e) => {
                      const newId = e.target.value;
                      setSelectedBalancePersonnelId(newId);
                      getLeaveBalance(newId);
                    }}
                    style={{
                      padding: '8px 12px',
                      fontSize: '13px',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e1',
                      backgroundColor: '#ffffff',
                      color: '#0f172a',
                      fontWeight: 500,
                      minWidth: '240px'
                    }}
                  >
                    <option value="">-- Select Personnel --</option>
                    {(personnelOptions.length > 0
                      ? personnelOptions
                      : Array.from(
                          new Map(
                            approvalInbox
                              .filter(i => i.army_personnel)
                              .map(i => [i.army_personnel.id, i.army_personnel])
                          ).values()
                        )
                    ).map(p => (
                      <option key={p.id} value={p.id}>
                        {p.full_name} ({p.rank || 'N/A'}) - ID #{p.army_id || p.id}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Individual Personnel Profile Banner */}
            {leaveBalance.personnel && (
              <div style={{ padding: '12px 16px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#1E40AF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '16px' }}>
                  👤
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e3a8a' }}>
                    {leaveBalance.personnel.full_name} <span style={{ fontWeight: 500, fontSize: '13px', color: '#3b82f6' }}>({leaveBalance.personnel.rank || 'Officer'})</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#475569' }}>
                    Army ID: <strong>{leaveBalance.personnel.army_id}</strong> | Selected Individual Personnel Calculated Record
                  </div>
                </div>
              </div>
            )}

            {/* Live Metric Summary Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Total Allocated Entitlement</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#1E40AF', marginTop: '4px' }}>
                  {leaveBalance.total || 0} Days
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Annual Cap per Regulations</div>
              </div>

              <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Approved Days Taken</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#dc2626', marginTop: '4px' }}>
                  {leaveBalance.taken || 0} Days
                </div>
                <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px' }}>Deducted from approved requests</div>
              </div>

              <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc' }}>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Carried Forward (Prev Year)</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#F59E0B', marginTop: '4px' }}>
                  {leaveBalance.carried_over || 0} Days
                </div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Expires Dec 31, {new Date().getFullYear()}</div>
              </div>

              <div style={{ padding: '16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f0fdf4' }}>
                <div style={{ fontSize: '12px', color: '#16A34A', fontWeight: 600, textTransform: 'uppercase' }}>Net Available Balance</div>
                <div style={{ fontSize: '24px', fontWeight: 700, color: '#16A34A', marginTop: '4px' }}>
                  {leaveBalance.remaining !== undefined ? leaveBalance.remaining : 0} Days
                </div>
                <div style={{ fontSize: '11px', color: '#16A34A', marginTop: '4px' }}>✓ Current usable balance</div>
              </div>
            </div>

            {/* Individual Breakdown Table per Leave Type */}
            <div style={{ marginBottom: '24px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>
                📋 Breakdown by Leave Type
              </h4>
              <div className="table-responsive">
                <table className="baseTable table-compact">
                  <thead>
                    <tr>
                      <th>Leave Type</th>
                      <th>Max Entitlement</th>
                      <th>Approved Days Taken</th>
                      <th>Carried Over</th>
                      <th>Remaining Balance</th>
                      <th>Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveEntitlements.length > 0 ? (
                      leaveEntitlements.map((ent, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 600, color: '#0f172a' }}>{ent.leave_type || 'Annual Leave'}</td>
                          <td>{ent.max_days || 0} Days</td>
                          <td style={{ color: ent.taken_days > 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>{ent.taken_days || 0} Days</td>
                          <td style={{ color: '#d97706' }}>{ent.carried_over_days || 0} Days</td>
                          <td style={{ color: '#16A34A', fontWeight: 700 }}>{ent.remaining_days ?? 0} Days</td>
                          <td>{ent.year || new Date().getFullYear()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', color: '#64748b', padding: '16px' }}>
                          No leave entitlement data loaded for this personnel.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Calculation Rules & Formula Card */}
            <div style={{ backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '8px' }}>
                💡 Calculation Formula:
              </h4>
              <div style={{ fontSize: '13px', color: '#334155', lineHeight: '1.6' }}>
                <code>Net Remaining Balance = (Max Entitlement + Carried Over Days) - Sum of Approved Leave Days</code>
                <br />
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  * Only leave requests with status <strong>APPROVED</strong> are deducted. Pending or rejected requests do not affect the balance.
                </span>
              </div>
            </div>

          </div>
        )}

        {activeLeaveTab === 'timeline' && (
          <div className="card-dense" style={{ padding: '24px', backgroundColor: '#ffffff', borderRadius: '8px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', marginBottom: '16px' }}>Leave Approval Timeline & Workflow Chain</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#16A34A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>1</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>Level 1: Department Commander Review</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Verified and approved department eligibility</div>
                </div>
              </div>
              <div style={{ borderLeft: '2px solid #e2e8f0', marginLeft: '15px', height: '20px' }}></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#1E40AF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>2</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>Level 2: HR & Attendance Office</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Balance deduction and final confirmation</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeLeaveTab === 'inbox' && (
          <div className="leaveRequestMain">
            <div className="baseTableWrap">

            {/* Header & Export Toolbar */}
            <div className="dFlex baseTableHd mb_16" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="txt20 fwMedium colorBlack">Leave Approval Inbox</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={exportToCSV}
                  style={{
                    backgroundColor: '#0f172a',
                    color: '#fff',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    borderRadius: '6px',
                    fontWeight: 500,
                    cursor: 'pointer'
                  }}
                >
                  <i className="fa fa-download"></i> Export CSV
                </button>
              </div>
            </div>

            {/* Filter Section */}
            <div style={{
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                {/* Search */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Search Personnel
                  </label>
                  <input
                    type="text"
                    placeholder="Name, Army ID, Rank..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                  />
                </div>

                {/* Status */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Leave Type */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Leave Type
                  </label>
                  <select
                    value={filterLeaveType}
                    onChange={(e) => { setFilterLeaveType(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                  >
                    <option value="all">All Leave Types</option>
                    {leaveTypes.map(lt => (
                      <option key={lt.id} value={lt.id}>{lt.name}</option>
                    ))}
                  </select>
                </div>

                {/* Force / Army Type */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    Force
                  </label>
                  <select
                    value={filterArmyType}
                    onChange={(e) => { setFilterArmyType(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                  >
                    <option value="all">All Forces</option>
                    {armyTypes.map(at => (
                      <option key={at.id} value={at.id}>{at.name}</option>
                    ))}
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => { setFilterStartDate(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                  />
                </div>

                {/* Date To */}
                <div>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '4px', textTransform: 'uppercase' }}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => { setFilterEndDate(e.target.value); setCurrentPage(1); }}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: '#fff' }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Showing <strong>{filteredApprovalInbox.length}</strong> of {approvalInbox.length} requests
                </span>
                <button
                  type="button"
                  onClick={resetFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <i className="fa fa-undo"></i> Reset Filters
                </button>
              </div>
            </div>

            {/* Bulk Action Sticky Bar */}
            {selectedIds.length > 0 && (
              <div style={{
                backgroundColor: '#0f172a',
                color: '#fff',
                padding: '12px 20px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
              }}>
                <div style={{ fontSize: '14px', fontWeight: 600 }}>
                  <span style={{ backgroundColor: '#2563eb', color: '#fff', padding: '2px 8px', borderRadius: '12px', marginRight: '8px' }}>
                    {selectedIds.length}
                  </span>
                  items selected for batch action
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => handleBulkAction('approve')}
                    style={{
                      backgroundColor: '#16a34a',
                      color: '#fff',
                      border: 'none',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <i className="fa fa-check"></i> Approve Selected
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkAction('reject')}
                    style={{
                      backgroundColor: '#fff7ed',
                      color: '#c2410c',
                      border: '1px solid #ffedd5',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <i className="fa fa-times"></i> Reject Selected
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBulkAction('delete')}
                    style={{
                      backgroundColor: '#fef2f2',
                      color: '#991b1b',
                      border: '1px solid #fecdd3',
                      padding: '6px 14px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}
                  >
                    <i className="fa fa-trash"></i> Delete Selected
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedIds([])}
                    style={{
                      backgroundColor: 'transparent',
                      color: '#94a3b8',
                      border: 'none',
                      padding: '6px 10px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Main Table */}
            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                    <th style={{ padding: '12px 16px', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                        style={{ cursor: 'pointer' }}
                      />
                    </th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Personnel</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Leave Type</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>From</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>To</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Document</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '12px 16px', fontWeight: 600 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedApprovalInbox.length > 0 ? (
                    paginatedApprovalInbox.map((item) => {
                      const isSelected = selectedIds.includes(item.id);
                      const canDeleteThis = item.can_delete || isManager || (item.status.toLowerCase() === 'pending' && item.army_personnel?.id === user.army_personnel?.id);
                      const displayStatusText = (item.status === 'pending' || item.workflow_status?.toLowerCase().includes('pending'))
                        ? 'Pending'
                        : (item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Unknown');

                      const isPending = item.status === 'pending' || item.workflow_status?.includes('Pending');
                      const isApproved = item.status === 'approved' || item.workflow_status === 'Approved';
                      const isRejected = item.status === 'rejected' || item.status === 'cancelled' || item.workflow_status === 'Rejected';

                      return (
                        <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', backgroundColor: isSelected ? '#f0f9ff' : undefined }}>
                          <td style={{ padding: '12px 16px' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectItem(item.id)}
                              style={{ cursor: 'pointer' }}
                            />
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>
                              {item.army_personnel?.full_name || 'N/A'}
                            </div>
                            {item.army_personnel?.rank && (
                              <div style={{ fontSize: '11px', color: '#64748b' }}>
                                {item.army_personnel.rank}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>{item.leave_type?.name || item.leave_type || 'N/A'}</td>
                          <td style={{ padding: '12px 16px' }}>{item.start_date}</td>
                          <td style={{ padding: '12px 16px' }}>{item.end_date}</td>
                          <td style={{ padding: '12px 16px' }}>
                            {item.document_path ? (
                              <a
                                href={item.document_path}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 8px',
                                  backgroundColor: '#f8fafc',
                                  color: '#2563eb',
                                  border: '1px solid #cbd5e1',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  textDecoration: 'none'
                                }}
                              >
                                <i className="fa fa-file-text-o" style={{ fontSize: '11px' }}></i> View Document
                              </a>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '12px' }}>No Doc</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: isPending ? '#d97706' : isApproved ? '#16a34a' : isRejected ? '#dc2626' : '#475569'
                            }}>
                              {displayStatusText}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                              {item.status.toLowerCase() === 'pending' && isPersonnel && item.army_personnel?.id === user.army_personnel?.id ? (
                                <button
                                  type="button"
                                  onClick={() => handleLeaveCancel(item.id)}
                                  style={{
                                    backgroundColor: '#f8fafc',
                                    color: '#64748b',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '5px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <i className="fa fa-ban" style={{ fontSize: '11px' }}></i> Cancel
                                </button>
                              ) : item.status.toLowerCase() === 'approved' && isManager ? (
                                <button
                                  type="button"
                                  onClick={() => handleRevoke(item.id)}
                                  style={{
                                    backgroundColor: '#f8fafc',
                                    color: '#475569',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '5px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <i className="fa fa-undo" style={{ fontSize: '11px' }}></i> Revoke
                                </button>
                              ) : item.can_approve && item.status.toLowerCase() === 'pending' ? (
                                <>
                                  {rejectingId === item.id ? (
                                    <div className="formInpt" style={{ display: 'flex', gap: '4px' }}>
                                      <input
                                        type="text"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Reason"
                                        style={{ padding: '4px 8px', fontSize: '12px', width: '100px' }}
                                      />
                                      <button className="btn btnPrimary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => handleReject(item.id)}>
                                        Submit
                                      </button>
                                      <button className="btn btnBlack" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => setRejectingId(null)}>
                                        X
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => handleApprove(item.id)}
                                        style={{
                                          backgroundColor: '#0f172a',
                                          color: '#ffffff',
                                          border: '1px solid #0f172a',
                                          borderRadius: '5px',
                                          padding: '5px 12px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '5px'
                                        }}
                                      >
                                        <i className="fa fa-check" style={{ fontSize: '11px' }}></i> Approve
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleReject(item.id)}
                                        style={{
                                          backgroundColor: '#fff7ed',
                                          color: '#c2410c',
                                          border: '1px solid #ffedd5',
                                          borderRadius: '5px',
                                          padding: '5px 12px',
                                          fontSize: '12px',
                                          fontWeight: 600,
                                          cursor: 'pointer',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '5px'
                                        }}
                                      >
                                        <i className="fa fa-times" style={{ fontSize: '11px' }}></i> Reject
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : null}

                              {/* Single Delete Option */}
                              {canDeleteThis && (
                                <button
                                  type="button"
                                  onClick={() => handleDelete(item.id)}
                                  title="Delete leave request"
                                  style={{
                                    backgroundColor: '#fef2f2',
                                    color: '#991b1b',
                                    border: '1px solid #fecdd3',
                                    borderRadius: '5px',
                                    padding: '5px 12px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: '11px' }}></i> Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                        No Leave Records found matching your filter criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        </div>
      )}
        {isPersonnel && (
          <div className="mt_40 dashGraphsSec">
            <div className="baseTableWrap fullWidth">
              <div className="dFlex baseTableHd mb_16">
                <h2 className="txt20 fwMedium colorBlack">Leave Reports</h2>
              </div>
              <div className="baseTableContainer">
                <table className="baseTable baseTableDepartment">
                  <thead>
                    <tr>
                      <th>Army ID</th>
                      <th>Name</th>
                      <th>Region / District</th>
                      <th>Army Type / Department</th>
                      <th>Leave Type</th>
                      <th>Total Leaves Taken</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaveReports.map((report, index) => (
                      <tr key={index}>
                        <td>{report.army_id}</td>
                        <td>{report.name}</td>
                        <td>{report.region}</td>
                        <td>{report.department}</td>
                        <td>{report.leave_type}</td>
                        <td>{report.total_leaves}</td>
                        <td>
                          <span className={getStatusClass(report.status)}>{report.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Reject Reason Modal */}
      {bulkRejectModalOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 600, color: '#1e293b' }}>
              Reject Selected Leave Requests ({selectedIds.length})
            </h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              Please enter a rejection reason that will be recorded for all selected leave requests.
            </p>
            <textarea
              rows="4"
              value={bulkRejectReasonText}
              onChange={(e) => setBulkRejectReasonText(e.target.value)}
              placeholder="Reason for bulk rejection..."
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '13px',
                marginBottom: '20px'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setBulkRejectModalOpen(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleBulkAction('reject', bulkRejectReasonText)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LeaveRequests;
